import React, { FC, Fragment, useState } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Box, Alert, Link } from '@mui/material';
import { MaterialPriceList, PriceListMaterial } from 'models/material';
import { useTranslation } from 'react-i18next';
import MaterialItem from './material-item.component';
import SortableHeaderLabel from 'components/shared/sortable-header-label/sortable-header-label.component';
import { OrderBy } from 'models/search-result';
import {
    DEFAULT_MATERIAL_PRICE_LIST_ORDER,
    MATERIAL_PRICE_LIST_SORT_FIELDS,
    sortPriceListMaterials,
    toggleOrderBy,
} from 'utils/price-list-sort';

type MaterialListProps = {
    materialPriceList?: MaterialPriceList;
    onDiscountedPriceChange: (materialDiscountedPrice: { id?: string; discountedPrice: string }) => void;
    freetextFilter: string;
    currency: string;
};
const MaterialListComponent: FC<MaterialListProps> = ({
    onDiscountedPriceChange,
    materialPriceList,
    freetextFilter,
    currency,
}: MaterialListProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory', 'actions-materials']);
    const [orderBy, setOrderBy] = useState<OrderBy>(DEFAULT_MATERIAL_PRICE_LIST_ORDER);

    const handleSortChange = (field: string) => {
        setOrderBy(toggleOrderBy(orderBy, field));
    };

    const filteredMaterials = (materialPriceList?.materials || []).filter(
        ({ description, materialCode, basePrice }) =>
            description.toLocaleLowerCase().includes(freetextFilter.toLocaleLowerCase()) ||
            materialCode?.toLocaleLowerCase().includes(freetextFilter.toLocaleLowerCase()) ||
            basePrice?.toString().includes(freetextFilter),
    );

    return (
        <Fragment>
            {materialPriceList?.materials?.length ? (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <SortableHeaderLabel
                                        field={MATERIAL_PRICE_LIST_SORT_FIELDS.MATERIAL_CODE}
                                        label={translate('actions-materials:CODE')}
                                        orderBy={orderBy}
                                        onSortChange={handleSortChange}
                                    />
                                </TableCell>
                                <TableCell>
                                    <SortableHeaderLabel
                                        field={MATERIAL_PRICE_LIST_SORT_FIELDS.DESCRIPTION}
                                        label={translate('common:DESCRIPTION')}
                                        orderBy={orderBy}
                                        onSortChange={handleSortChange}
                                    />
                                </TableCell>
                                <TableCell>{translate('client-directory:DATE_ADDED')}</TableCell>
                                <TableCell>{translate('common:BASE_PRICE')}</TableCell>
                                <TableCell align="right">{translate('client-directory:CLIENT_PRICE')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Fragment>
                                {sortPriceListMaterials(filteredMaterials, orderBy).map(
                                    (material: PriceListMaterial) => (
                                        <MaterialItem
                                            key={material._id}
                                            material={material}
                                            onDiscountedPriceChange={onDiscountedPriceChange}
                                            currency={currency}
                                        />
                                    ),
                                )}
                            </Fragment>
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box p={2}>
                    <Alert severity="info">
                        {translate('client-directory:NO_MATERIALS_ON_PRICE_LIST')}
                        &nbsp;
                        <Link href={`/lab-settings/material-settings`}>
                            {translate('lab-settings:HANDLE_MATERIAL_LISTS')}
                        </Link>
                    </Alert>
                </Box>
            )}
        </Fragment>
    );
};

export default MaterialListComponent;
